import i18n from './i18n';
import { Amplify } from 'aws-amplify';
// Must be a static import so the OAuth listener is registered before Amplify
// processes any OAuth callback in the URL (dynamic import would be too late).
// Safe in mock mode — no OAuth events fire when Amplify is not configured.
import 'aws-amplify/auth/enable-oauth-listener';
import { amplifyConfig } from './amplifyconfiguration';
import { effectiveAuthMode, isMockAuthEnabled } from '@/config/authMode';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { I18nextProvider } from 'react-i18next';
import { useEffect } from 'react';
import { createRoot } from 'react-dom/client';
import { ToastContainer } from 'react-toastify';
import useZoomDetection from '@/utils/zoomDetection';
import { LicenseInfo } from '@mui/x-license';
import { SidebarNavigationProvider } from '@/contexts/sidebarNavigationContext';
import { LoadingProvider } from '@/contexts/LoadingContext';
import ConfirmationProvider from '@/provider/ConfirmationProvider';
import { StyledEngineProvider } from '@mui/system';
import Pages from './routes';
import { ThemeComponentProvider } from '@/components/shared/Theme/ThemeComponent';
import '@/components/main.scss';
import '@/components/modern-overrides.scss';
import { GlobalFiltersProvider } from '@/hooks/useGlobalFilters';

// Configure AWS Amplify only in Cognito mode.
if (amplifyConfig) {
  Amplify.configure(amplifyConfig);
} else if (isMockAuthEnabled) {
  console.info('[AUTH_MODE] Running UI in local mock mode. Cognito is disabled in this runtime.');
} else {
  console.warn(
    `[AUTH_MODE] No Amplify configuration available. Effective auth mode is "${effectiveAuthMode}".`,
  );
}

function App() {
  const isZoomedIn = useZoomDetection();

  useEffect(() => {
    if (import.meta.env.VITE_MUI_LICENSE_KEY) {
      LicenseInfo.setLicenseKey(import.meta.env.VITE_MUI_LICENSE_KEY);
    }
  }, []);

  useEffect(() => {
    if (!import.meta.env.PROD) {
      return undefined;
    }

    const preventContextMenu = (event: MouseEvent) => {
      event.preventDefault();
    };

    const preventRestrictedKeys = (event: KeyboardEvent) => {
      if (
        event.key === 'F12' ||
        (event.ctrlKey && event.shiftKey && event.key === 'I') ||
        (event.ctrlKey && event.shiftKey && event.key === 'J') ||
        (event.ctrlKey && event.key === 'U')
      ) {
        event.preventDefault();
      }
    };

    document.addEventListener('contextmenu', preventContextMenu);
    document.addEventListener('keydown', preventRestrictedKeys);

    return () => {
      document.removeEventListener('contextmenu', preventContextMenu);
      document.removeEventListener('keydown', preventRestrictedKeys);
    };
  }, []);

  return (
    <I18nextProvider i18n={i18n}>
      <StyledEngineProvider injectFirst>
        <ThemeComponentProvider>
          <ConfirmationProvider />
          <LoadingProvider>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <SidebarNavigationProvider>
                <GlobalFiltersProvider>
                  <div className={isZoomedIn ? 'App browserZoom' : 'App'}>
                    <ToastContainer stacked />
                    <Pages />
                  </div>
                </GlobalFiltersProvider>
              </SidebarNavigationProvider>
            </LocalizationProvider>
          </LoadingProvider>
        </ThemeComponentProvider>
      </StyledEngineProvider>
    </I18nextProvider>
  );
}

const container = document.getElementById('root');
const root = createRoot(container!);
root.render(<App />);
